/**
 * Plans API Module
 * All plan-related API endpoints
 */

import { api } from './client.js';

/**
 * Plans API endpoints
 */
export const plansApi = {
  /**
   * Get all plans with optional filters and pagination
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 10, max: 100)
   * @param {boolean} params.is_active - Filter by active status
   * @param {string} params.search - Search term for name or description
   * @returns {Promise<Object>} Plans data with pagination
   */
  getAll: (params = {}) => {
    const cleanParams = Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});

    const queryParams = new URLSearchParams(cleanParams).toString();
    const endpoint = `/admin/plans${queryParams ? `?${queryParams}` : ''}`;
    return api.get(endpoint);
  },

  /**
   * Get plan by ID
   * @param {string|number} id - Plan ID
   * @returns {Promise<Object>} Plan data
   */
  getById: (id) => {
    if (!id) {
      return Promise.reject(new Error('Plan ID is required'));
    }
    return api.get(`/admin/plans/${id}`);
  },

  /**
   * Get active plans
   * @returns {Promise<Array>} Active plans array
   */
  getActive: () => {
    return api.get('/admin/plans/active');
  },

  /**
   * Create new plan
   * @param {Object} planData - Plan data
   * @param {string} planData.name - Plan name (required)
   * @param {string} [planData.description] - Plan description
   * @param {number} planData.price - Price (required)
   * @param {string} [planData.billing_interval] - Billing interval (Monthly, Yearly)
   * @param {number} [planData.usage_limit] - Usage limit
   * @param {number} [planData.max_users] - Maximum users
   * @param {string|Array} [planData.features] - Features (string or array)
   * @param {boolean} [planData.is_active] - Active status
   * @returns {Promise<Object>} Created plan data
   */
  create: (planData) => {
    if (!planData || !planData.name) {
      return Promise.reject(new Error('Plan name is required'));
    }
    return api.post('/admin/plans', planData);
  },

  /**
   * Update plan
   * @param {string|number} id - Plan ID
   * @param {Object} planData - Updated plan data (all fields optional)
   * @returns {Promise<Object>} Updated plan data
   */
  update: (id, planData) => {
    if (!id) {
      return Promise.reject(new Error('Plan ID is required'));
    }
    if (!planData || Object.keys(planData).length === 0) {
      return Promise.reject(new Error('Plan data is required'));
    }
    return api.put(`/admin/plans/${id}`, planData);
  },

  /**
   * Delete plan (soft delete)
   * @param {string|number} id - Plan ID
   * @returns {Promise<Object>} Deletion confirmation
   */
  delete: (id) => {
    if (!id) {
      return Promise.reject(new Error('Plan ID is required'));
    }
    return api.delete(`/admin/plans/${id}`);
  },

  /**
   * Toggle plan active status
   * @param {string|number} id - Plan ID
   * @returns {Promise<Object>} Updated plan data
   */
  toggleActive: (id) => {
    if (!id) {
      return Promise.reject(new Error('Plan ID is required'));
    }
    return api.patch(`/admin/plans/${id}/toggle-active`, {});
  },

  /**
   * Get plan statistics overview
   * @returns {Promise<Object>} Plan statistics
   */
  getStats: () => {
    return api.get('/admin/plans/stats/overview');
  },

  getTrialSettings: () => {
    return api.get('/admin/plans/trial-settings');
  },

  updateTrialSettings: (settingsData) => {
    return api.put('/admin/plans/trial-settings', settingsData);
  },
};

export default plansApi;
